/*
 * Aviator API client.
 * Auth comes ONLY from the main app's localStorage session:
 *   tokenl  -> Authorization: Bearer <tokenl>
 *   userid  -> sent as `user-id` header and as `userId` in bet payloads
 *   dev_id  -> device headers
 */
const API_URL = (
  process.env.REACT_APP_API_URL_AVIATOR_NODE ||
  "https://api.babaclubs.co/api/users"
).replace(/\/$/, "");

export const getMainAppAuth = () => ({
  token: localStorage.getItem("tokenl"),
  userid: localStorage.getItem("userid"),
  devId: localStorage.getItem("dev_id"),
});

export const getUserId = () => localStorage.getItem("userid") || "";

const parseResponse = async (response) => {
  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text || "Request failed" };
  }

  // Backend sometimes answers HTTP 200 with {"success":"0","message":"INSUFFICIENT BALANCE"}
  const failed =
    data &&
    typeof data === "object" &&
    (data.success === false || data.success === "0" || data.success === 0 || data.success === "false");

  if (!response.ok || failed) {
    const message =
      data?.message ||
      data?.error ||
      data?.errors?.[0]?.msg ||
      data?.errors?.[0]?.message ||
      `Request failed with status ${response.status}`;
    const error = new Error(String(message));
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const request = async (path, options = {}) => {
  const { token, userid, devId } = getMainAppAuth();
  if (!token || !userid) {
    throw new Error("Please log in to the main application first.");
  }

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("user-id", userid);
  if (devId) {
    headers.set("device-id", devId);
    headers.set("x-device-id", devId);
  }

  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  return parseResponse(response);
};

// Short-lived GET de-dupe so rapid polling never stacks identical requests.
const inflight = new Map();

export const apiClient = {
  get: (path, params) => {
    // userId is sent on every request (query for GET, body for POST/PUT)
    const query = new URLSearchParams({ ...(params || {}), userId: getUserId() });
    const url = `${path}?${query}`;
    if (inflight.has(url)) return inflight.get(url);
    const promise = request(url).finally(() => inflight.delete(url));
    inflight.set(url, promise);
    return promise;
  },
  post: (path, body) =>
    request(path, {
      method: "POST",
      body: JSON.stringify({ userId: getUserId(), ...(body ?? {}) }),
    }),
  put: (path, body) =>
    request(path, {
      method: "PUT",
      body: JSON.stringify({ userId: getUserId(), ...(body ?? {}) }),
    }),
};

export { API_URL };
